// Randevular › Genel: tüm randevular tek düz listede (design-principles §3).
// Arama (hasta adı / TC / telefon) + tarih aralığı + durum çipleri → çerçevesiz
// tablo. Satıra tıklayınca takvimdeki aynı randevu drawer'ı açılır.
import React, { useEffect, useRef, useState } from "react";
import useDebouncedSearch from "../../../hooks/useDebouncedSearch";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Avatar,
  Skeleton,
  Tooltip,
  ButtonBase,
  alpha,
} from "@mui/material";
import { FaCalendarAlt } from "react-icons/fa";
import { DataTableCard } from "../../../components/organisms";
import { ChoiceChipGroup, FormDatePicker, SearchField } from "../../../components/atoms";
import { COLORS, TYPO, ACCENTS } from "../../../styles/uiConstants";
import { useClinicTheme } from "../../../theme";
import { useSearchAppointmentsMutation } from "../../../store/secretaryApi";
import { initialsOf, avatarTone, maskTc, fullDateTime } from "../PatientsPage/patientListFormat";
import { ExamLiveBadge } from "./examLive";
import useTerms from "../../../hooks/useTerms";
import { clinical } from "../../../terms";

const SEARCH_DEBOUNCE_MS = 350;
const ROWS_PER_PAGE_OPTIONS = [25, 50, 100];
const COLS = 6;

const STATUS_FILTERS = [
  { value: "all", label: "Tümü" },
  { value: "scheduled", label: "Planlandı" },
  { value: "in_examination", label: clinical.inExam },
  { value: "completed", label: "Tamamlandı" },
  { value: "cancelled", label: "İptal" },
  { value: "no_show", label: "Gelmedi" },
];

const STATUS_META = {
  scheduled: { label: "Planlandı", tone: ACCENTS.blue },
  completed: { label: "Tamamlandı", tone: COLORS.successDark },
  cancelled: { label: "İptal", tone: COLORS.error },
  no_show: { label: "Gelmedi", tone: COLORS.warningDark },
};

const typeLabels = (terms) => ({ ilk_muayene: terms.firstVisitTitle, kontrol: terms.followUp });

const TR_MONTHS = ["Oca", "Şub", "Mar", "Nis", "May", "Haz", "Tem", "Ağu", "Eyl", "Eki", "Kas", "Ara"];
const hhmm = (d) => d.toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" });
const dayStart = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
const dayLabel = (d) => {
  const diff = Math.round((dayStart(d) - dayStart(new Date())) / 86400000);
  if (diff === 0) return "Bugün";
  if (diff === 1) return "Yarın";
  if (diff === -1) return "Dün";
  const sameYear = d.getFullYear() === new Date().getFullYear();
  return `${d.getDate()} ${TR_MONTHS[d.getMonth()]}${sameYear ? "" : ` ${d.getFullYear()}`}`;
};

const headSx = {
  fontWeight: 600,
  fontSize: TYPO.caption,
  letterSpacing: "0.04em",
  textTransform: "uppercase",
  color: COLORS.slate500,
  bgcolor: COLORS.white,
  borderBottom: `1px solid ${COLORS.slate200}`,
  py: 1.25,
  whiteSpace: "nowrap",
};
const cellSx = { py: 1.25, borderBottom: `1px solid ${COLORS.slate100}` };
const mainText = { fontSize: TYPO.body, fontWeight: 500, color: COLORS.slate800, lineHeight: 1.35 };
const subText = { fontSize: TYPO.caption, color: COLORS.slate500, lineHeight: 1.35 };
const dash = <Typography sx={{ ...subText, color: COLORS.slate300 }}>—</Typography>;

// Hasta: populate edilmiş kayıt; yoksa randevu anındaki ad (snapshot) ad/soyad'a bölünür.
const patientOf = (a, terms = clinical) => {
  const p = a.patient && typeof a.patient === "object" ? a.patient : null;
  if (p && (p.name || p.surname)) {
    return { person: p, name: `${p.name || ""} ${p.surname || ""}`.trim() };
  }
  const snap = String(a?.snapshot?.patientName || "").trim();
  const parts = snap.split(/\s+/);
  return {
    person: { name: parts.slice(0, -1).join(" ") || parts[0] || "", surname: parts.length > 1 ? parts[parts.length - 1] : "" },
    name: snap || terms.patient,
  };
};

const doctorOf = (a) => {
  const s = a.snapshot || {};
  if (!s.doctorName) return null;
  return `${s.doctorTitle ? `${s.doctorTitle} ` : ""}${s.doctorName}`;
};

export default function AppointmentListView({ token, scope, reloadKey, onOpen, canCreate, onCreate }) {
  const { palette } = useClinicTheme();
  const terms = useTerms();
  const [searchAppointments] = useSearchAppointmentsMutation();

  const [search, setSearch] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [status, setStatus] = useState("all");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [counts, setCounts] = useState(null);
  const [isFetching, setIsFetching] = useState(true);
  const [error, setError] = useState("");
  const seqRef = useRef(0);

  const { debounced, pending: searchPending } = useDebouncedSearch(
    search.trim(),
    SEARCH_DEBOUNCE_MS,
  );
  // Yazarken (debounce) ve istek sürerken iskelet.
  const busy = isFetching || searchPending;

  useEffect(() => {
    if (!token) return;
    const seq = ++seqRef.current;
    setIsFetching(true);
    setError("");
    searchAppointments({
      token,
      q: debounced || undefined,
      from: from || undefined,
      to: to || undefined,
      status: status === "all" ? undefined : status,
      page: page + 1,
      limit: rowsPerPage,
      mine: scope === "mine",
    })
      .unwrap()
      .then((res) => {
        if (seq !== seqRef.current) return;
        setItems(res?.data?.list || []);
        setTotal(res?.data?.total || 0);
        setCounts(res?.data?.counts || null);
      })
      .catch((e) => {
        if (seq !== seqRef.current) return;
        setItems([]);
        setTotal(0);
        setError(e?.data?.message || "Randevular alınamadı.");
      })
      .finally(() => {
        if (seq === seqRef.current) setIsFetching(false);
      });
  }, [token, debounced, from, to, status, page, rowsPerPage, scope, reloadKey, searchAppointments]);

  const resetPage = (fn) => (v) => {
    fn(v);
    setPage(0);
  };

  // "Gelmedi" yalnız kaydı varsa görünür; seçiliyken de kalır.
  const statusOptions = STATUS_FILTERS.filter(
    (s) => s.value !== "no_show" || status === "no_show" || (counts?.no_show ?? 0) > 0
  ).map((s) => {
    const label = s.value === "in_examination" ? terms.inExam : s.label;
    return { value: s.value, label: counts ? `${label} · ${counts[s.value] ?? 0}` : label };
  });

  const hasFilters = Boolean(debounced || from || to || status !== "all");
  const clearFilters = () => {
    setSearch("");
    setFrom("");
    setTo("");
    setStatus("all");
    setPage(0);
  };
  const inlineLink = (label, onClick) => (
    <ButtonBase
      onClick={onClick}
      sx={{ font: "inherit", color: palette.primary, fontWeight: 600, verticalAlign: "baseline" }}
    >
      {label}
    </ButtonBase>
  );
  const emptyMessage = error ? (
    error
  ) : hasFilters ? (
    <>
      {debounced ? `“${debounced}” aramanızla eşleşen randevu yok. ` : "Seçili filtrelere uyan randevu yok. "}
      {inlineLink("Filtreleri temizleyin", clearFilters)}
      {canCreate ? <> veya {inlineLink("yeni randevu ekleyin", onCreate)}.</> : "."}
    </>
  ) : canCreate ? (
    <>Henüz randevu yok. {inlineLink("Yeni randevu ekleyin", onCreate)}.</>
  ) : (
    "Henüz randevu yok."
  );

  return (
    <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", bgcolor: COLORS.white }}>
      {/* Araç satırı */}
      <Box
        sx={{
          px: { xs: 2, md: 4 },
          pt: 2.5,
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          flexWrap: "wrap",
          flexShrink: 0,
        }}
      >
        <SearchField
          placeholder={`${terms.patient} adı, TC veya telefon ile ara`}
          value={search}
          onChange={(v) => {
            setSearch(v);
            setPage(0);
          }}
          autoComplete="off"
          sx={{ width: { xs: "100%", sm: 340 } }}
        />
        <FormDatePicker
          placeholder="Başlangıç"
          value={from}
          onChange={resetPage(setFrom)}
          maxDate={to || undefined}
          sx={{ width: 170 }}
        />
        <FormDatePicker
          placeholder="Bitiş"
          value={to}
          onChange={resetPage(setTo)}
          minDate={from || undefined}
          sx={{ width: 170 }}
        />
      </Box>

      {/* Durum çipleri */}
      <Box sx={{ px: { xs: 2, md: 4 }, pt: 2, pb: 2, flexShrink: 0 }}>
        <ChoiceChipGroup
          ariaLabel="Randevu durumu"
          options={statusOptions}
          value={status}
          onChange={resetPage(setStatus)}
        />
      </Box>

      <DataTableCard
        loading={Boolean(busy)}
        variant="flat"
        empty={!busy && items.length === 0}
        emptyIcon={<FaCalendarAlt />}
        emptyTitle={error ? "Randevular alınamadı" : "Randevu bulunamadı"}
        emptyMessage={emptyMessage}
        page={page}
        rowsPerPage={rowsPerPage}
        totalCount={total}
        onPageChange={(e, p) => setPage(p)}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(0);
        }}
        rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
      >
        <Table stickyHeader size="small" sx={{ tableLayout: "fixed", minWidth: 980 }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ ...headSx, pl: { xs: 2, md: 4 }, width: "26%" }}>{terms.patient}</TableCell>
              <TableCell sx={{ ...headSx, width: "16%" }}>Tarih</TableCell>
              <TableCell sx={{ ...headSx, width: "18%" }}>{terms.doctor}</TableCell>
              <TableCell sx={{ ...headSx, width: "11%" }}>Tip</TableCell>
              <TableCell sx={{ ...headSx, width: "17%" }}>Şikayet</TableCell>
              <TableCell sx={{ ...headSx, width: "12%", pr: { xs: 2, md: 4 } }}>Durum</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {busy ? (
              [...Array(10)].map((_, i) => (
                <TableRow key={`sk-${i}`}>
                  {Array.from({ length: COLS }).map((__, c) => (
                    <TableCell key={c} sx={{ ...cellSx, ...(c === 0 ? { pl: { xs: 2, md: 4 } } : {}) }}>
                      <Skeleton variant="text" />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : items.length ? (
              items.map((a) => (
                <AppointmentRow key={a._id} appointment={a} palette={palette} onOpen={onOpen} />
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={COLS} sx={{ height: 1, py: 0, border: 0 }} />
              </TableRow>
            )}
          </TableBody>
        </Table>
      </DataTableCard>
    </Box>
  );
}

function AppointmentRow({ appointment: a, palette, onOpen }) {
  const terms = useTerms();
  const { person: patient, name } = patientOf(a, terms);
  const tone = avatarTone(patient);
  const start = new Date(a.startAt);
  const end = a.endAt ? new Date(a.endAt) : null;
  const status = String(a.status || "scheduled").toLowerCase();
  const isInExam = status === "in_examination";
  const meta = STATUS_META[status] || STATUS_META.scheduled;
  const isCancelled = status === "cancelled";
  const doctor = doctorOf(a);

  return (
    <TableRow
      hover
      onClick={() => onOpen(a)}
      sx={{
        cursor: "pointer",
        "&:hover": { bgcolor: `${alpha(palette.primary, 0.03)} !important` },
      }}
    >
      {/* Hasta */}
      <TableCell sx={{ ...cellSx, pl: { xs: 2, md: 4 } }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}>
          <Avatar
            sx={{ width: 34, height: 34, fontSize: 12.5, fontWeight: 600, bgcolor: alpha(tone, 0.14), color: tone }}
          >
            {initialsOf(patient)}
          </Avatar>
          <Box sx={{ minWidth: 0 }}>
            <Typography
              sx={{
                ...mainText,
                fontWeight: 600,
                color: isCancelled ? COLORS.slate500 : COLORS.slate800,
              }}
              noWrap
            >
              {name}
            </Typography>
            {patient.tcNo ? (
              <Typography sx={subText} noWrap>
                TC {maskTc(patient.tcNo)}
              </Typography>
            ) : null}
          </Box>
        </Box>
      </TableCell>

      {/* Tarih */}
      <TableCell sx={cellSx}>
        <Tooltip title={fullDateTime(a.startAt)} arrow placement="top">
          <Box sx={{ display: "inline-block" }}>
            <Typography sx={mainText}>{dayLabel(start)}</Typography>
            <Typography sx={{ ...subText, fontVariantNumeric: "tabular-nums" }}>
              {hhmm(start)}
              {end ? `–${hhmm(end)}` : ""}
            </Typography>
          </Box>
        </Tooltip>
      </TableCell>

      {/* Doktor */}
      <TableCell sx={cellSx}>
        {doctor ? (
          <Typography sx={mainText} noWrap>
            {doctor}
          </Typography>
        ) : (
          dash
        )}
      </TableCell>

      {/* Tip */}
      <TableCell sx={cellSx}>
        <Typography sx={mainText} noWrap>
          {typeLabels(terms)[a.appointmentType] || terms.firstVisitTitle}
        </Typography>
      </TableCell>

      {/* Şikayet */}
      <TableCell sx={cellSx}>
        {a.complaint ? (
          <Typography sx={{ ...mainText, color: COLORS.slate600 }} noWrap title={a.complaint}>
            {a.complaint}
          </Typography>
        ) : (
          dash
        )}
      </TableCell>

      {/* Durum */}
      <TableCell sx={{ ...cellSx, pr: { xs: 2, md: 4 } }}>
        {isInExam ? (
          <ExamLiveBadge />
        ) : (
          <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.75 }}>
            <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: meta.tone, flexShrink: 0 }} />
            <Typography sx={{ ...mainText, color: COLORS.slate700 }} noWrap>
              {meta.label}
            </Typography>
          </Box>
        )}
      </TableCell>
    </TableRow>
  );
}
